'use client';

/**
 * WhatsAppAttachmentRow - paperclip + base64 chip list
 *
 * Reused by both WhatsAppCompose (used in rfq-chat / vendor-chat /
 * project-chat / whatsapp-send-section) and the bespoke WhatsApp tab in
 * messenger-tabs-section. Owns nothing; parent provides the
 * attachments + setter.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useRef } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WhatsAppMediaAttachment } from '@/lib/api/types';

interface WhatsAppAttachmentRowProps {
  attachments: WhatsAppMediaAttachment[];
  onAttachmentsChange: (next: WhatsAppMediaAttachment[]) => void;
  disabled?: boolean;
}

export function WhatsAppAttachmentRow({
  attachments,
  onAttachmentsChange,
  disabled,
}: WhatsAppAttachmentRowProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: WhatsAppMediaAttachment[] = [...attachments];
    for (const file of Array.from(files)) {
      const data = await fileToBase64(file);
      next.push({
        filename: file.name,
        mimetype: file.type || 'application/octet-stream',
        data,
      });
    }
    onAttachmentsChange(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAt = (idx: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
      >
        <Paperclip className="h-3.5 w-3.5 mr-1" />
        Attach
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {attachments.map((att, idx) => (
        <span
          key={`${att.filename}-${idx}`}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs"
          title={`${att.mimetype} · ${formatSize(att.data)}`}
        >
          <span className="truncate max-w-[120px]">{att.filename}</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            disabled={disabled}
            onClick={() => removeAt(idx)}
            aria-label={`Remove ${att.filename}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function formatSize(base64: string): string {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  const bytes = Math.floor((base64.length * 3) / 4) - padding;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
